import { FC } from 'react'
import { Case, Switch, When } from 'react-if'
import { GuildScore } from '@/atoms/GuildScore'
import { ClosedCaptionsBadge, ContentRatingBadge, HighDefinitionBadge } from '@/atoms/MetadataBadge'
import { CaptionSM } from '@/atoms/Text'
import { UnifiedProjectHoverContent } from '@/services/ProjectsService/types'
import { isDefined } from '@/utils/primitives/generics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface ContentMetadataProps {
  hoverContent: UnifiedProjectHoverContent
}

export const ContentMetadata: FC<ContentMetadataProps> = ({ hoverContent }) => {
  const { t } = useTranslate('watch')

  return (
    <>
      <div className="flex flex-row flex-wrap items-center justify-center gap-1 md:justify-start lg:gap-2">
        {hoverContent?.originalReleaseDate && (
          <>
            <CaptionSM className="sm:photon-caption-md">
              {new Date(hoverContent.originalReleaseDate).getUTCFullYear().toString()}
            </CaptionSM>
            <CaptionSM className="sm:photon-caption-md">•</CaptionSM>
          </>
        )}
        <CaptionSM className="sm:photon-caption-md whitespace-nowrap">
          <Switch>
            <Case condition={hoverContent?.__typename === 'ContentSeries'}>
              <Translate i18nKey="tvSeries" t={t}>
                TV Series
              </Translate>
            </Case>
            <Case condition={hoverContent?.__typename === 'ContentMovie'}>
              <Translate i18nKey="movie" t={t}>
                Movie
              </Translate>
            </Case>
            <Case condition={hoverContent?.__typename === 'ContentSpecial'}>
              <Translate i18nKey="special" t={t}>
                Special
              </Translate>
            </Case>
            <Case condition={hoverContent?.__typename === 'ContentEpisode'}>
              <Translate i18nKey="episode" t={t}>
                Episode
              </Translate>
            </Case>
          </Switch>
        </CaptionSM>
        <GenreList genres={hoverContent?.genres?.filter(isDefined)} />
      </div>
      <BadgeList hoverContent={hoverContent} />
    </>
  )
}

const GenreList: FC<{ genres?: string[] }> = ({ genres }) => {
  if (!Array.isArray(genres) || genres.length === 0) return null

  return (
    <>
      <CaptionSM className="sm:photon-caption-md">•</CaptionSM>
      {genres.map((genre, index) => (
        <div className="flex gap-1 lg:gap-2" key={genre}>
          <CaptionSM className="sm:photon-caption-md">{genre}</CaptionSM>
          <When condition={index < genres.length - 1}>
            <CaptionSM className="sm:photon-caption-md">•</CaptionSM>
          </When>
        </div>
      ))}
    </>
  )
}

const BadgeList: FC<{ hoverContent: UnifiedProjectHoverContent }> = ({ hoverContent }) => (
  <div className="flex flex-row flex-wrap items-center justify-start gap-2">
    {hoverContent?.ratings && hoverContent?.ratings?.length > 0 && (
      <ContentRatingBadge
        contentRating={hoverContent.ratings[0]}
        className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl"
      />
    )}
    <HighDefinitionBadge
      slug={('project' in hoverContent && hoverContent?.project?.slug) || ''}
      className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl"
    />
    <ClosedCaptionsBadge className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl" />
    {'project' in hoverContent && hoverContent?.project?.highestScore && (
      <GuildScore guildScore={hoverContent.project.highestScore} />
    )}
  </div>
)
